/**
 * Card Payment Input Component
 */
import { getDeviceInfo } from "../../Utils/deviceInfo";
import { getEncrypDecryptStr } from "../../Utils/encryptionDecryption";
import React, { useEffect, useState, FC, HTMLAttributes } from "react";
import { useDispatch, useSelector } from "react-redux";
import { initPayment } from "../../Redux/Actions";
import {
  cardNumberFormat,
  formatCvc,
  formatExpiry,
  getCardType,
  isCardValid,
  isExpiryInvalid,
} from "../../Utils/cardTypeValidator";
import config from "../../Config";
import { RootState } from "../../Redux/Reducers";
import LocalStorageHelper from "../../Utils/LocalStorageHelper";
import "./styles.scss";
import InfoIcon from "../../Assets/images/info.png";
import loaderIcon from "./../../Assets/images/ellipLoader1.gif";
import { ENCRYPTED_CARD_DETAILS } from "../../Constants/localStorageKeys";
import { PAYMENT_TYPE_CONSTANTS } from "../../Constants";
import { clevertapEventPush } from "../../Utils/common";

const CardPaymentBox: FC<any> = (props: any) => {
  const dispatch = useDispatch();
  const [state, setState] = useState({
    cvv: "",
    expiry: "",
    number: "",
    cardType: "",
    isCardNumberValid: true,
    isCardMonthInvalid: "",
    cvvLength: 3,
    isCardCVVInvalid: "",
  });

  /** Icon CDN's for cards */
  const cc_icons: any = {
    VISA: config.CDN_SERVER_URL + "image/visa.png",
    MASTERCARD: config.CDN_SERVER_URL + "image/mastercard.png",
    AMEX: config.CDN_SERVER_URL + "image/amex.png",
    MAESTRO: config.CDN_SERVER_URL + "image/maestro.png",
    DebitCard: config.CDN_SERVER_URL + "image/sample_card.png",
  };

  const { prePaymentData, storeData, loader } = useSelector((state: RootState) => ({
    prePaymentData: state.paymentMethodReducer.prePaymentData,
    storeData: state.storeReducer.store,
    loader: state.commonReducer.pageLoaderVisibility,
  }));

  /**
   * UseEffect for prefilled card data
   */
  useEffect(() => {
    let newState = { ...state };
    if (LocalStorageHelper.get(ENCRYPTED_CARD_DETAILS)) {
      let cardDetails = getEncrypDecryptStr(LocalStorageHelper.get(ENCRYPTED_CARD_DETAILS), "decrypt");
      cardDetails = JSON.parse(cardDetails);
      newState = {
        ...state,
        cvv: "",
        expiry: cardDetails?.cExpMonth + "/" + cardDetails?.cExpYear.slice(-2),
        number: cardNumberFormat(cardDetails?.cNum),
        cardType: getCardType(cardDetails?.cNum),
        cvvLength: getCardType(cardDetails?.cNum) === "AMEX" ? 4 : 3,
      };
    }
    setState(newState);
  }, []);

  const handleInputChange = (key: string, value: any, event: any) => {
    const newState = { ...state };
    if (key === "number") {
      newState[key] = cardNumberFormat(value);
      newState.cardType = getCardType(value);
      newState.cvvLength = newState.cardType === "AMEX" ? 4 : 3;
    } else if (key === "expiry") {
      newState.expiry = formatExpiry(event);
    } else if (key === "cvv") {
      newState.cvv = formatCvc(value);
    }
    setState(newState);
  };

  const validateAll = () => {
    const newState = { ...state };
    let isAllValid = true;
    if (!state.number) {
      newState.isCardNumberValid = false;
      isAllValid = false;
    }
    if (!state.expiry) {
      newState.isCardMonthInvalid = "Enter a valid expiry";
      isAllValid = false;
    }
    if (state.cvvLength !== state.cvv.length) {
      newState.isCardCVVInvalid = "Enter a valid CVV";
      isAllValid = false;
    }
    setState(newState);
    return isAllValid;
  };

  const validateCardNumber = (number: any) => {
    const newState = { ...state };
    const isValid = isCardValid(number);
    newState.isCardNumberValid = isValid;
    setState(newState);
    return isValid;
  };

  const validateCardMonth = (number: any) => {
    const newState = { ...state };
    newState.isCardMonthInvalid = isExpiryInvalid(number) ? "true" : "";
    setState(newState);
  };

  const validateCardCVV = (number: string | any[]) => {
    const newState = { ...state };
    newState.isCardCVVInvalid = number.length !== state.cvvLength ? "Enter valid CVV" : "";
    setState(newState);
  };

  const onPayBtnClick = () => {
    if (validateAll() && validateCardNumber(state.number)) {
      const data = {
        payment_option_id: 3,
        phone: storeData?.owner?.phone,
        orderID: prePaymentData?.orderID,
        paymentMethod: PAYMENT_TYPE_CONSTANTS.CARD,
        merchantID: prePaymentData?.merchantID,
        deviceType: getDeviceInfo().OSName,
        browserType: getDeviceInfo().BrowserName,
        userAgent: getDeviceInfo().userAgent,
        source: "premium",
        cNum: state.number.split(" ").join(""),
        cCvv: state.cvv,
        cName: "Dotpe",
        cExpMonth: state.expiry.split("/")[0].trim(),
        cExpYear: "20" + state.expiry.split("/")[1].trim(),
        merchantName: `Digital Showroom - ${storeData?.store_info?.name}`,
        credit_used: props.creditsUsed || 0,
      };
      const encryptCCDetails = getEncrypDecryptStr(JSON.stringify(data), "encrypt");
      localStorage.setItem(ENCRYPTED_CARD_DETAILS, JSON.stringify(encryptCCDetails));
      dispatch(initPayment(data));
    }
  };

  return (
    <section className="card-payment-box pa4">
      <h2 className="mt0 mb4">{`Pay${props.paymentAmount && `  ₹${props.paymentAmount}`}`}</h2>
      <div className="card-wrapper">
        <div className="card flex">
          <div className="form-wrap">
            <div className="form-group flex flex-column">
              <label>Card Number</label>
              <input
                style={{
                  maxWidth: 300,
                  width: "100%",
                }}
                type="text"
                inputMode="numeric"
                className="form-control"
                placeholder="XXXX XXXX XXXX XXXX"
                onChange={(e) => handleInputChange("number", e.target.value, e)}
                onBlur={() => validateCardNumber(state.number)}
                value={state.number}
                pattern="[0-9.]+"
              />
              {!state.isCardNumberValid && <small className="error">Enter valid card number</small>}
            </div>
            <div className="inline-form-wrap">
              <div className="form-group flex flex-column">
                <label>Expires</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="MM/YY"
                  value={state.expiry}
                  pattern="\d*"
                  maxLength={7}
                  onChange={(e) => handleInputChange("expiry", e.target.value, e)}
                  onBlur={() => validateCardMonth(state?.expiry.split(" ").join(""))}
                />
                {state.isCardMonthInvalid && <small className="error">{state.isCardMonthInvalid}</small>}
              </div>
              <div className="form-group flex flex-column">
                <label>CVV</label>
                <input
                  type="password"
                  value={state.cvv}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="form-control"
                  placeholder={state.cvvLength === 4 ? "* * * *" : "* * *"}
                  maxLength={state.cvvLength}
                  onChange={(e) => handleInputChange("cvv", e.target.value, e)}
                  onBlur={(e) => validateCardCVV(state.cvv)}
                />
                {state.isCardCVVInvalid && <small className="error">{state.isCardCVVInvalid}</small>}
              </div>
            </div>
          </div>
          <div className="bottom-wrap flex items-end">
            <div className="icon-wrapper flex">
              <img
                loading="lazy"
                src={cc_icons[state.cardType] || config.CDN_SERVER_URL + "image/sample_card.png"}
                alt="cardType"
              />
            </div>
          </div>
        </div>
        <div className="flex mt3 mb2 items-center relative">
          <img src={InfoIcon} style={{ width: ".75rem", top: -1 }} className="mr2 relative" />
          <p className="mv0 b f7">
            We respect your privacy. Your card details are saved on your browser and not our system.
          </p>
        </div>
        <button
          onClick={onPayBtnClick}
          className={`cta-btn ${!state.isCardNumberValid ? "o-50" : ""}`}
          disabled={loader}
        >
          {!loader ? 
            `Pay${props.paymentAmount && `  ₹${props.paymentAmount}`}`
           : 
            <img src={loaderIcon} className="img h1" />
          }
        </button>
      </div>
    </section>
  );
};

export default CardPaymentBox;
